import React, {
  useMemo,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  GridOptions,
  ModuleRegistry,
  AllCommunityModule,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useTheme } from '../../../hooks/useTheme';
import { palette } from '../../../utils/palette';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface SupportTicket {
  id: number;
  user_id?: number;
  email?: string;
  name?: string;
  subject: string;
  message: string;
  category?: string;
  status: string;
  priority: string;
  assigned_to?: number;
  response?: string;
  responded_at?: string;
  responded_by?: number;
  created_at: string;
  updated_at: string;
}

interface SupportTicketsGridProps {
  tickets: SupportTicket[];
  searchValue?: string;
  onRespond?: (ticketId: number) => void;
  onUpdateStatus?: (ticketId: number, status: string) => void;
}

const SupportTicketsGrid = forwardRef<any, SupportTicketsGridProps>(
  ({ tickets, searchValue = '', onRespond, onUpdateStatus }, ref) => {
    const { colors } = useTheme();
    const gridRef = React.useRef<AgGridReact>(null);
    const gridApiRef = React.useRef<any>(null);

    // Only render on web
    if (Platform.OS !== 'web') {
      return null;
    }

    // Filter tickets based on search value
    const filteredTickets = React.useMemo(() => {
      if (!searchValue.trim()) return tickets;
      const lowerSearch = searchValue.toLowerCase();
      return tickets.filter((ticket) => {
        const searchFields = [
          ticket.email,
          ticket.name,
          ticket.subject,
          ticket.message,
          ticket.status,
          ticket.priority,
          ticket.category,
          ticket.id?.toString(),
        ];
        return searchFields.some(
          (field) => field && String(field).toLowerCase().includes(lowerSearch)
        );
      });
    }, [tickets, searchValue]);

    const columnDefs: ColDef[] = useMemo(
      () => [
        { field: 'id', headerName: 'ID', width: 80, sortable: true },
        {
          field: 'email',
          headerName: 'Email',
          flex: 1,
          sortable: true,
          filter: true,
        },
        {
          field: 'name',
          headerName: 'Name',
          flex: 1,
          sortable: true,
          filter: true,
        },
        {
          field: 'subject',
          headerName: 'Subject',
          flex: 1,
          sortable: true,
          filter: true,
        },
        {
          field: 'message',
          headerName: 'Message',
          flex: 2,
          sortable: true,
          filter: true,
          cellRenderer: (params: any) => {
            const message = params.value || '';
            return message.length > 50
              ? `${message.substring(0, 50)}...`
              : message;
          },
        },
        {
          field: 'category',
          headerName: 'Category',
          width: 120,
          sortable: true,
          filter: true,
          cellRenderer: (params: any) => {
            return params.value || 'general';
          },
        },
        {
          field: 'status',
          headerName: 'Status',
          width: 120,
          sortable: true,
          filter: true,
          cellRenderer: (params: any) => {
            const status = params.value || 'open';
            const statusColors: { [key: string]: string } = {
              open: palette.brightBlue,
              in_progress: palette.lightOrange,
              resolved: palette.cyanLimeGreen,
              closed: palette.darkGrayBlue,
            };
            return (
              <span
                style={{
                  color: statusColors[status] || palette.darkGrayBlue,
                  fontWeight: '600',
                  textTransform: 'capitalize',
                }}
              >
                {status.replace('_', ' ')}
              </span>
            );
          },
        },
        {
          field: 'priority',
          headerName: 'Priority',
          width: 100,
          sortable: true,
          filter: true,
          cellRenderer: (params: any) => {
            const priority = params.value || 'normal';
            const priorityColors: { [key: string]: string } = {
              low: palette.darkGrayBlue,
              normal: palette.brightBlue,
              high: palette.lightOrange,
              urgent: palette.brightMatteRed,
            };
            return (
              <span
                style={{
                  color: priorityColors[priority] || palette.brightBlue,
                  fontWeight: '600',
                  textTransform: 'capitalize',
                }}
              >
                {priority}
              </span>
            );
          },
        },
        {
          field: 'created_at',
          headerName: 'Created',
          width: 150,
          sortable: true,
          filter: true,
          cellRenderer: (params: any) => {
            if (!params.value) return '';
            const date = new Date(params.value);
            return (
              date.toLocaleDateString() +
              ' ' +
              date.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })
            );
          },
        },
        {
          headerName: 'Actions',
          width: 200,
          cellRenderer: (params: any) => {
            const ticket = params.data as SupportTicket;
            return (
              <div
                style={{ display: 'flex', gap: '4px', alignItems: 'center' }}
              >
                {onRespond && (
                  <button
                    onClick={() => onRespond(ticket.id)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: colors.primary,
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '10px',
                    }}
                    title="Respond"
                  >
                    Respond
                  </button>
                )}
                {onUpdateStatus && (
                  <select
                    value={ticket.status}
                    onChange={(e) => onUpdateStatus(ticket.id, e.target.value)}
                    style={{
                      padding: '4px 6px',
                      fontSize: '10px',
                      borderRadius: '4px',
                      border: `1px solid ${colors.border}`,
                      backgroundColor: colors.card,
                      color: colors.text,
                      cursor: 'pointer',
                    }}
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                )}
              </div>
            );
          },
        },
      ],
      [onRespond, onUpdateStatus, colors]
    );

    const gridOptions: GridOptions = useMemo(
      () => ({
        theme: 'legacy',
        defaultColDef: {
          resizable: true,
          sortable: true,
          filter: true,
        },
        rowHeight: 50,
        headerHeight: 40,
        suppressCellFocus: true,
        suppressRowClickSelection: true,
        animateRows: true,
        pagination: false,
        paginationPageSize: 50,
        domLayout: 'autoHeight',
      }),
      []
    );

    useEffect(() => {
      if (gridRef.current && gridRef.current.api) {
        gridApiRef.current = gridRef.current.api;
      }
    }, []);

    useImperativeHandle(ref, () => ({
      refresh: () => {
        if (gridApiRef.current) {
          gridApiRef.current.refreshCells({ force: true });
        }
      },
      exportData: () => {
        if (gridApiRef.current) {
          gridApiRef.current.exportDataAsCsv();
        }
      },
    }));

    return (
      <View style={styles.container}>
        <div
          className="ag-theme-alpine"
          style={
            {
              width: '100%',
              height: 'auto',
              '--ag-header-background-color': colors.card,
              '--ag-header-foreground-color': colors.text,
              '--ag-odd-row-background-color': colors.background,
              '--ag-row-background-color': colors.card,
              '--ag-row-hover-color': colors.border,
              '--ag-border-color': colors.border,
              '--ag-header-cell-hover-background-color': colors.border,
              '--ag-input-focus-border-color': colors.primary,
              '--ag-selected-row-background-color': colors.primary + '20',
              '--ag-range-selection-background-color': colors.primary + '10',
              '--ag-input-disabled-background-color': colors.background,
              '--ag-input-disabled-border-color': colors.border,
              '--ag-input-disabled-foreground-color': colors.secondary,
              '--ag-panel-background-color': colors.card,
              '--ag-tool-panel-background-color': colors.card,
              '--ag-side-button-selected-background-color': colors.primary,
              '--ag-side-button-selected-foreground-color': palette.white,
            } as React.CSSProperties & { [key: string]: any }
          }
        >
          <style>{`
          .ag-theme-alpine .ag-body-viewport::-webkit-scrollbar,
          .ag-theme-alpine .ag-body-horizontal-scroll::-webkit-scrollbar {
            display: none;
          }
          .ag-theme-alpine .ag-body-viewport,
          .ag-theme-alpine .ag-body-horizontal-scroll {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .ag-theme-alpine {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
            font-size: 12px;
          }
          .ag-theme-alpine .ag-header {
            border-bottom: 1px solid ${colors.border};
            font-size: 13px;
            font-weight: 600;
          }
          .ag-theme-alpine .ag-header-cell {
            border-right: 1px solid ${colors.border};
            font-size: 13px;
            font-weight: 600;
          }
          .ag-theme-alpine .ag-header-cell-text {
            font-size: 13px;
            font-weight: 600;
          }
          .ag-theme-alpine .ag-row {
            border-bottom: 1px solid ${colors.border};
            font-size: 12px;
            font-weight: 400;
          }
          .ag-theme-alpine .ag-cell {
            border-right: 1px solid ${colors.border};
            font-size: 12px;
            font-weight: 400;
            line-height: 1.5;
          }
          .ag-theme-alpine .ag-cell-value {
            font-size: 12px;
            font-weight: 400;
          }
          .ag-theme-alpine input,
          .ag-theme-alpine select,
          .ag-theme-alpine button {
            font-size: 12px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
          }
        `}</style>
          <AgGridReact
            ref={gridRef}
            rowData={filteredTickets}
            columnDefs={columnDefs}
            gridOptions={gridOptions}
          />
        </div>
      </View>
    );
  }
);

const styles = StyleSheet.create({
  container: {
    width: '100%',
    marginTop: 16,
  },
});

SupportTicketsGrid.displayName = 'SupportTicketsGrid';

export default SupportTicketsGrid;
